/* eslint-disable no-unused-expressions */
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { AI_BACKEND_API, API_TOKEN, API_URL } from "../../Config/secrets";

const initialState = {
  isLoading: false,
  error: null,
  // Data is stored by page index, e.g. { [pageNumber]: BrandRequestRow[] }
  brandRequests: {},
  totalBrandRequests: 0,
};

const slice = createSlice({
  name: "brandRequests",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getBrandRequestsSuccess(state, action) {
      const { page, total, data } = action.payload;
      state.error = null;
      state.totalBrandRequests = total;
      state.brandRequests = {
        ...state.brandRequests,
        [page]: data,
      };
      state.isLoading = false;
    },
    resetBrandRequests: () => initialState,
  },
});

export default slice.reducer;

export const { resetBrandRequests } = slice.actions;

const header = {
  Authorization: API_TOKEN,
};

export function getBrandRequests(
  page = 1,
  query = "",
  take = 10,
  status,
  match_type,
  created_at,
  hideLoader = false
) {
  return async (dispatch) => {
    try {
      if (!hideLoader) {
        dispatch(slice.actions.startLoading());
      }

      const payload = { skip: page, take };

      if (query) payload.query = query;
      if (status) payload.status = status;
      if (match_type) payload.match_type = match_type;
      if (created_at) payload.created_at = created_at;

      const response = await axios.post(
        `${API_URL}/brand-requests/get-brand-requests`,
        payload,
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        dispatch(
          slice.actions.getBrandRequestsSuccess({
            page,
            total: response?.data?.data?.count || 0,
            data: response?.data?.data?.data || [],
          })
        );
        return response?.data?.data?.data || [];
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to fetch brand requests", { autoClose: 3000 });
      return [];
    } catch (error) {
      console.error("GET BRAND REQUESTS ERROR", error);
      toast.error(error?.message || "Failed to fetch brand requests", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function createBrandRequest(formData) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      // Extract brand_name and website from formData.brand_info
      const payload = {
        brand_name: formData.brand_info?.brand_name,
        website: formData.brand_info?.website,
        status: formData?.find_matches?.type === 'standard' ? 'Matches Pending' : 'Matches Processing',
        match_type: formData.find_matches?.type,
        data: {
          ...formData,
          brand_info: {
            ...formData.brand_info,
            markets_of_interest: formData.brand_info?.markets_of_interest?.length ? formData.brand_info?.markets_of_interest?.map((market) => market.label) : [],
          }
        },
      };

      if (formData?.find_matches?.type === 'standard') {
        payload.data = {
          ...formData,
          find_matches: {}
        }
      }

      const response = await axios.post(
        `${API_URL}/brand-requests/create-brand-request`,
        payload,
        { headers: header }
      );

      axios.post(`${AI_BACKEND_API}/brand_request`, { id: response?.data?.data?.id })


      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Brand request created successfully", { autoClose: 3000 });
        dispatch(slice.actions.resetBrandRequests());
        return response?.data;
      }


      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to create brand request", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      console.error("CREATE BRAND REQUEST ERROR", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to create brand request", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function updateBrandRequest(id, formData, hideToast = false, user_name) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const payload = {
        id,
        brand_name: formData.brand_info?.brand_name,
        website: formData.brand_info?.website,
        status: formData?.find_matches?.type === 'skipped' ? 'Matches Pending' : 'Matches Processing',
        match_type: formData.find_matches?.type,
        data: {
          ...formData,
          brand_info: {
            ...formData.brand_info,
            markets_of_interest: formData.brand_info?.markets_of_interest?.length ? formData.brand_info?.markets_of_interest?.map((market) => market.label) : [],
          }
        },
      };

      const response = await axios.post(
        `${API_URL}/brand-requests/update-brand-request`,
        { data: payload, updated_by: user_name },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        axios.post(`${AI_BACKEND_API}/brand_request`, { id: response?.data?.data?.id })

        if (!hideToast) toast.success(response?.data?.message || "Brand request updated successfully", { autoClose: 3000 });
        dispatch(slice.actions.resetBrandRequests());
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to update brand request", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      console.error("UPDATE BRAND REQUEST ERROR", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update brand request", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

export function markHubspotTriggered(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.post(
        `${API_URL}/brand-requests/mark-hubspot-triggered`,
        { id },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      return response?.data;
    } catch (error) {
      console.error("MARK HUBSPOT TRIGGERED ERROR", error);
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

/**
 * Delete Brand Request
 * Backend contract: DELETE /brand-requests/delete-brand-request
 * - body: { id }
 */
export function deleteBrandRequest(id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.delete(
        `${API_URL}/brand-requests/delete-brand-request`,
        { params: { id } },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Brand request deleted successfully", { autoClose: 3000 });
        dispatch(slice.actions.resetBrandRequests());
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to delete brand request", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      console.error("DELETE BRAND REQUEST ERROR", error);
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to delete brand request",
        { autoClose: 3000 }
      );
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}

/**
 * Approve Brand Request
 * Backend contract: POST /brand-requests/approve-brand-request
 * - body: { id, approved_by_admin }
 */
export function approveBrandRequest(id, approved_by_admin) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      const response = await axios.post(
        `${API_URL}/brand-requests/approve-brand-request`,
        { id, approved_by_admin },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success("Brand request approved successfully", { autoClose: 3000 });
        dispatch(slice.actions.resetBrandRequests());
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to approve brand request", { autoClose: 3000 });
      return response?.data;
    } catch (error) {
      console.error("APPROVE BRAND REQUEST ERROR", error);
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to approve brand request",
        { autoClose: 3000 }
      );
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}


export function adminUpdateBrandRequest(id, formData, user_name) {
  return async (dispatch, getState) => {
    try {
      dispatch(slice.actions.startLoading());
      console.log('user_name', user_name);
      const response = await axios.post(
        `${API_URL}/brand-requests/admin-update-brand-request`,
        { data: { id, ...formData }, updated_by: user_name },
        { headers: header }
      );

      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Brand request updated successfully", { autoClose: 3000 });
        return response?.data;
      }

      dispatch(slice.actions.hasError(response?.data?.message));
      toast.error(response?.data?.message || "Failed to update brand request", { autoClose: 3000 });
      return response?.data;
    }
    catch (error) {
      console.error("ADMIN UPDATE BRAND REQUEST ERROR", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to update brand request", { autoClose: 3000 });
      dispatch(slice.actions.hasError(error?.response?.data));
      return error?.response?.data;
    }
  };
}